import type { SearchMode } from '../searchMode';
import {
  availableSearchParametersFor,
  isRecentlyViewedCuratedSetOrStack,
  SearchModeEnum,
  SearchModeFactory,
} from '../searchMode';
import {
  isFilm,
  SearchQueryFactory,
  type SearchParameterName,
  type SearchParameters,
} from '../searchParameters';
import { ASSET_TYPE_AND_FAMILY_RULES } from './availabilityRules';
import type {
  SearchParameterScope,
  SearchParameterSiteExclusions,
} from './siteExclusions';

const searchModeFactory = new SearchModeFactory();
const queryFactory = new SearchQueryFactory();

/**
 * What one search can carry, worked out once.
 *
 * The mode is a property of the search, not of each question asked about it —
 * it is settled the moment the search exists. Deriving it per question meant a
 * search stating ten parameters derived its own mode ten times, which is what
 * `clearUnavailable` did on every call.
 *
 * So the mode and the set of parameters it leaves open are resolved in the
 * constructor, and every question after that is a lookup. The asset-type and
 * family rule stays lazy: it is cheap, and a caller asking about two
 * parameters should not pay to evaluate sixty.
 *
 * Tied to the search it was built for. A different search needs a new one —
 * `SearchParameterAvailability#for` is how you get it.
 */
export class AvailableParameters {
  readonly #searchParameters: SearchParameters;

  readonly #siteExclusions: SearchParameterSiteExclusions;

  readonly #mode: SearchMode;

  readonly #allowedByMode: ReadonlySet<SearchParameterName>;

  constructor(
    searchParameters: SearchParameters,
    siteExclusions: SearchParameterSiteExclusions
  ) {
    this.#searchParameters = searchParameters;
    this.#siteExclusions = siteExclusions;
    this.#mode = searchModeFactory.fromSearchParameters(searchParameters);
    this.#allowedByMode = new Set(
      availableSearchParametersFor(this.#mode.kind)
    );
  }

  /**
   * The kind of search this is.
   *
   * Exposed because availability already had to work it out. A caller that
   * needs both — the filter panel wants the mode for its heading and
   * availability for its controls — would otherwise derive it twice.
   */
  get mode(): SearchMode {
    return this.#mode;
  }

  /**
   * Whether a parameter applies to this search.
   *
   * Two independent concerns: the mode, which has the final say on what it
   * hides — sort in a curated set, mood in a more-like-this search, imageurl
   * outside search-by-image — and the search's asset type and family.
   *
   * The site decides first: a parameter it excludes is never offered, and one
   * it offers only in listed scopes is offered exactly there.
   *
   * An unlisted parameter is unconstrained, so it is available by default.
   */
  has(name: SearchParameterName): boolean {
    if (this.#siteExclusions.excludedParams.includes(name)) return false;
    if (!this.#allowedByMode.has(name)) return false;

    const scopes = this.#siteExclusions.availableOnlyIn?.[name];
    if (scopes) return scopes.some((scope) => this.#isInScope(scope));

    const rule = ASSET_TYPE_AND_FAMILY_RULES.find(({ params }) =>
      params.includes(name)
    );

    return rule ? rule.isAvailable(this.#searchParameters) : true;
  }

  #isInScope({ assettypes, families }: SearchParameterScope): boolean {
    const { assettype, family } = this.#searchParameters;

    return (
      assettypes.includes(assettype) && (!families || families.includes(family))
    );
  }

  /**
   * Whether this search offers filtering at all.
   *
   * Coarser than `has`, and deliberately separate from it: a search can be a
   * fixed set of assets — recently viewed, or a stack or curated set viewed
   * without a phrase — in which case there is nothing to narrow, whatever the
   * per-parameter rules say. A search-by-image search scoped to film is the
   * same: the one thing it could narrow by, it cannot.
   *
   * These two answers do not agree today. `PARAMETERS_HIDDEN_BY_MODE` has a
   * curated set hiding only `imageurl`, `sort`, `mood` and `license`, so `has`
   * still offers orientations and people on a search this says offers nothing.
   * The rules are colocated so the disagreement is visible; reconciling them
   * means deciding which is right, which is a product question.
   */
  get offersFilters(): boolean {
    if (isRecentlyViewedCuratedSetOrStack(this.#mode, this.#searchParameters)) {
      return false;
    }

    return !(
      this.#mode.kind === SearchModeEnum.SEARCH_BY_IMAGE &&
      isFilm(this.#searchParameters)
    );
  }

  /** The search with every parameter it can no longer carry cleared out. */
  clearUnavailable(): SearchParameters {
    const stated = Object.keys(
      queryFactory.fromSearchParameters(this.#searchParameters)
    ) as SearchParameterName[];

    const unavailable = stated.filter((name) => !this.has(name));
    if (unavailable.length === 0) return this.#searchParameters;

    return this.#searchParameters.clear(...unavailable);
  }
}
